import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { DemosScore } from '../../../components/LeaderboardComponents/DemosScore';
import { TransparentButton } from '../../../components/TransparentButton';
import {
	ILeaderboardDemosFilter,
	LeaderboardDemosGroupBy,
	useLeaderboardDemos,
} from '../../../entities/Leaderboard/useDemos';
import { FetchStatus } from '../../../entities/utils/useStatesOnFetch';
import { useInterval } from '../../../hooks/useInterval';
import { useQuotaTeamAutocompleteQuery } from '../../../queries/Aida/useQuotaTeamAutocomplete';
import { styleSheet } from '../styles';
import { DailySlider } from './DailySlider';

let isFirstLoading = true;

export const Demos: React.FC = () => {
	const userSession = useUserSession();
	const isSalesCoffeeAccount = !!userSession?.account?.isLevitateSalesCoffeeAccount;
	const { data: quotaTeams, isPending: isQuotaTeamsPending } = useQuotaTeamAutocompleteQuery({
		enabled: isSalesCoffeeAccount,
	});
	const hasQuotaTeams = isSalesCoffeeAccount && !isQuotaTeamsPending && (quotaTeams?.length ?? 0) > 0;

	const [filter, setFilter] = useState<ILeaderboardDemosFilter>({
		day: moment().format('YYYY-MM-DD'),
	});

	// Effective groupBy: user's explicit choice, or the data-driven default once autocomplete resolves.
	// Non-Sales-Coffee accounts skip the autocomplete entirely and default to Vertical immediately.
	const groupBy: LeaderboardDemosGroupBy | undefined =
		filter.groupBy ??
		(!isSalesCoffeeAccount ? 'Vertical' : isQuotaTeamsPending ? undefined : hasQuotaTeams ? 'QuotaTeam' : 'Vertical');

	const { get, data, empty, status } = useLeaderboardDemos();

	useEffect(() => {
		if (groupBy === undefined) {
			return;
		}
		get({ filter: { ...filter, groupBy } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter, groupBy]);

	useEffect(() => {
		if (status === FetchStatus.Done) {
			isFirstLoading = false;
		}
	}, [status]);

	useInterval(() => {
		if (groupBy === undefined) {
			return;
		}
		get({ filter: { ...filter, groupBy } });
	}, 30000);

	const renderEmptyState = (
		<div className={css(styleSheet.noLeaders)}>
			<div className='no-leaders-header'>No data yet.</div>
		</div>
	);

	const handleDateChange = (value: string) => {
		setFilter(prev => ({ ...prev, day: value }));
	};

	const handleGroupByChange = (next: LeaderboardDemosGroupBy) => () => {
		if (groupBy === next) {
			return;
		}
		setFilter(prev => ({ ...prev, groupBy: next }));
	};

	const renderFilter = (
		<div className={css(styleSheet.demosFilterRow)}>
			<DailySlider onChange={handleDateChange} />
			{hasQuotaTeams && groupBy ? (
				<TransparentButton
					className={css(styleSheet.groupByToggleLink)}
					onClick={handleGroupByChange(groupBy === 'QuotaTeam' ? 'Vertical' : 'QuotaTeam')}
				>
					{groupBy === 'QuotaTeam' ? 'Show by vertical' : 'Show by quota team'}
				</TransparentButton>
			) : null}
		</div>
	);

	const groupLabel = groupBy === 'QuotaTeam' ? 'Quota Teams' : 'Verticals';

	const renderList = () => {
		const emptyState = empty ? renderEmptyState : null;
		return (
			<DemosScore
				groupLabel={groupLabel}
				renderEmptyState={isFirstLoading ? emptyState : null}
				renderFilter={renderFilter}
				scores={data}
			/>
		);
	};

	const isLoading = status !== FetchStatus.Done || groupBy === undefined;

	return (
		<div className={css(styleSheet.leaderboardContainer)}>
			<div className={css(styleSheet.boardsContainer)}>
				{isLoading && isFirstLoading ? <LoadingSpinner /> : renderList()}
			</div>
		</div>
	);
};
